import React, { useState, MouseEvent } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { Waypoint, DESCRIPTION } from '@fiverr-private/gig_page_waypoint_navigation';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Box } from '@fiverr-private/theme';
import { TextButton } from '@fiverr-private/button';
import { Heading } from '@fiverr-private/typography';
import SafePureComponent from '../shared/SafePureComponent';
import WithImpressions from '../shared/withImpressions';
import { validateDescription } from '../../utils/mainComponentsValidator';
import { sanitize } from '../../utils/sanitization';
import { getCurrentContent } from '../../utils/ugc/getCurrentContent';
import { isTranslated } from '../../utils/ugc/isTranslated';
import { BQ_GIG_DESCRIPTION_SEE_MORE } from '../../utils/bigQueryEvents';
import TranslatedBadge from '../shared/TranslatedBadge';
import { shouldShowSignUpForm } from '../../utils/shouldShowSignUpForm';
import { triggerSignUp } from '../../utils/signUp';
import SellerTranslationBadgeWrapper from '../shared/SellerTranslationBadgeWrapper';
import { isFieldTranslatedBySeller } from '../../utils/ugc/isFieldTranslatedBySeller';
import { Ugc } from '../../types/ugc';
import Metadata from './Metadata';
import { AttributeData } from './Metadata/utils';
import { descriptionContent } from './style.ve.css';

const createMarkup = (data: string | null) => (data ? { __html: data } : null);
const CLASSNAME = 'gig-description';

interface DescriptionData {
  content?: string;
}

interface DescriptionProps {
  showTranslatedBadge?: boolean;
  description?: DescriptionData;
  metadataAttributes?: AttributeData[];
  collectImpressions?: () => void;
}

export const Description = ({
  description,
  metadataAttributes,
  showTranslatedBadge = false,
  collectImpressions,
}: DescriptionProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const { biEvents, pathfinderWrapper, isBusinessLayout } = useGigPageContext();

  const onEnterWaypoint = () => {
    collectImpressions?.();
  };

  const toggleCollapsed = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!showInfo) {
      biEvents.sendBigQueryEvent({ eventName: BQ_GIG_DESCRIPTION_SEE_MORE });

      if (shouldShowSignUpForm()) {
        triggerSignUp({
          biEvents,
          pathfinderWrapper,
          options: { source: 'gig_page_description' },
        });
      }
    }

    setShowInfo((prev) => !prev);
  };

  if (!validateDescription({ description })) {
    return null;
  }

  const showContent = classNames(descriptionContent, 'description-content-new', { show: showInfo });
  const classes = classNames(CLASSNAME, { 'with-translated-badge': showTranslatedBadge });
  const { content } = description as DescriptionData;
  const sanitizedContent = createMarkup(sanitize(content ?? ''));
  const showKey = showInfo ? 'see_less' : 'see_more';

  const titleCopy = isBusinessLayout
    ? 'gig_page_perseus.top_nav.page_sections.theService'
    : 'gig_page_perseus.description.title';

  return (
    <>
      <Waypoint source={DESCRIPTION} />
      <Container className={classes} backgroundColor="white">
        <Stack as="header" direction="column" gap="0">
          <Heading
            as="h2"
            size={{ default: 'h_xs', sm: 'h_sm' }}
            paddingBottom={showTranslatedBadge ? '0' : { default: '3', sm: '6' }}
          >
            <SellerTranslationBadgeWrapper translationKey="description">
              <I18n k={titleCopy} />
            </SellerTranslationBadgeWrapper>
          </Heading>
          {showTranslatedBadge && <TranslatedBadge />}
        </Stack>
        {sanitizedContent && (
          <Container className="description-wrapper" position="relative" paddingBottom="5">
            <Waypoint onEnter={onEnterWaypoint} />
            <Box
              className={showContent}
              display={showInfo ? 'block' : { default: 'webkitBox', sm: 'block' }}
              webkitLineClamp={9}
              webkitBoxOrient="vertical"
              overflow="hidden"
              color="grey_1000"
              overflowWrap="breakWord"
              boxSizing="borderBox"
              fontSize={{ default: 'b_sm', sm: 'b_md' }}
              gap="3"
              dangerouslySetInnerHTML={sanitizedContent}
              lineHeight={{ default: 'b_sm', sm: 'b_md' }}
            />
            <Box className="cover" display="none" />
            <Box display={{ default: 'block', sm: 'none' }} paddingTop="3">
              <TextButton size="sm" intent="primary" onClick={toggleCollapsed}>
                <I18n k={`gig_page_perseus.description.${showKey}`} />
              </TextButton>
            </Box>
          </Container>
        )}
        <Metadata metadataAttributes={metadataAttributes ?? []} />
      </Container>
    </>
  );
};

const mapStateToProps = ({ ugc }: { ugc: Ugc }) => {
  const { description } = getCurrentContent({ ugc });
  const isFieldFromSellerTranslations = isFieldTranslatedBySeller(ugc, 'description');
  const isContentTranslated = isTranslated(ugc);

  return {
    description,
    showTranslatedBadge: isContentTranslated && !isFieldFromSellerTranslations,
  };
};

export const ConnectedDescription = connect(mapStateToProps)(Description);

const impressionConfig = {
  DOMSelector: `.${CLASSNAME} .description-content-new`,
  eventKey: 'gig_description',
};

export default SafePureComponent(WithImpressions(ConnectedDescription, impressionConfig));
